"use client"

import { Component, type ReactNode } from "react"

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error boundary that catches Clerk initialization failures.
 * 
 * When Clerk is configured with production keys for a specific domain,
 * it will fail to initialize on other origins (like v0 preview).
 * This boundary catches those errors and shows a helpful message
 * instead of a blank screen.
 */
export class ClerkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[ClerkErrorBoundary] Caught error:", error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      const isClerkDomainError = 
        this.state.error?.message?.includes("Production Keys") ||
        this.state.error?.message?.includes("domain") ||
        this.state.error?.message?.includes("Origin")

      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-[hsl(42,40%,99%)] px-4">
          <div className="flex max-w-md flex-col items-center gap-5 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100">
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="text-amber-600"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <div className="flex flex-col gap-2">
              <h1 className="text-lg font-semibold text-[hsl(25,15%,12%)]">
                {isClerkDomainError 
                  ? "Preview Environment Detected" 
                  : "Authentication Error"}
              </h1>
              <p className="text-sm text-[hsl(25,15%,45%)] leading-relaxed">
                {isClerkDomainError ? (
                  <>
                    This app is configured for production use on{" "}
                    <strong className="text-[hsl(25,15%,25%)]">referralflowhq.com</strong>.
                    <br /><br />
                    The v0 preview cannot authenticate against production Clerk keys.
                    To test locally, use development keys or deploy to the production domain.
                  </>
                ) : (
                  "An error occurred while initializing authentication. Please refresh the page or try again later."
                )}
              </p>
            </div>

            <div className="mt-2 flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="rounded-xl bg-[hsl(25,45%,35%)] px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[hsl(25,45%,30%)] transition-colors"
              >
                Refresh Page
              </button>
              {isClerkDomainError && (
                <a
                  href="https://referralflowhq.com"
                  className="rounded-xl border border-[hsl(34,18%,85%)] bg-white px-5 py-2.5 text-sm font-medium text-[hsl(25,15%,25%)] shadow-sm hover:bg-[hsl(40,30%,96%)] transition-colors"
                >
                  Go to Production
                </a>
              )}
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
